import React, { useEffect } from "react";
import BrandMark from "../components/BrandMark.jsx";
import NavBar from "../components/NavBar.jsx";
import "../styles/bespoke.css";

const RESEARCH_CARDS = [
  {
    name: "The PHP Group",
    date: "21 October 2026",
    summary: "Published security fixes and advised PHP users to update or check with their software vendor.",
    href: "https://www.php.net/archive/2026.php#2026-10-21-5",
  },
  {
    name: "UK National Cyber Security Centre",
    date: "Published 21 October; updated 23 October 2026",
    summary: "Advised organisations to find PHP systems, prioritise internet-facing services, install updates and check for signs of compromise where appropriate.",
    href: "https://www.ncsc.gov.uk/news/critical-security-issue-affecting-php",
  },
  {
    name: "CERT-EU",
    date: "Updated 23 October 2026",
    summary: "Recommended promptly patching affected systems and considering isolation when immediate patching is not possible.",
    href: "https://www.cert.europa.eu/publications/security-advisories/2026-018/",
  },
];

const ASSESSMENT_STEPS = [
  {
    number: "01",
    title: "Tell us about your website",
    description: "Share the website address and what you know about its developer or hosting company. Never send passwords or access credentials.",
  },
  {
    number: "02",
    title: "We agree what can be checked",
    description: "Our team will review your enquiry and agree what can be assessed. The scope and required authorisation are confirmed before any technical work begins.",
  },
  {
    number: "03",
    title: "We review the agreed evidence",
    description: "After scope, evidence and written authorisation are agreed, the assessment considers the relevant website, software and update information.",
  },
  {
    number: "04",
    title: "You get a clear report",
    description: "If the work goes ahead, the proposed report explains what was checked, what is known, what could not be confirmed and practical next steps.",
  },
];

const TECHNICAL_SOURCES = [
  ["PHP security announcement", "The PHP Group, 21 October 2026", "https://www.php.net/archive/2026.php#2026-10-21-5"],
  ["CVE-2026-109618 advisory", "PHP source repository (GHSA-w8hw-w9fx-j79w)", "https://github.com/php/php-src/security/advisories/GHSA-w8hw-w9fx-j79w"],
  ["CVE-2026-109619 advisory", "PHP source repository (GHSA-6wmf-4rv5-p3rp)", "https://github.com/php/php-src/security/advisories/GHSA-6wmf-4rv5-p3rp"],
  ["PHP informational advisory for CVE-2026-115560", "The PHP Group, 23 October 2026", "https://www.php.net/archive/2026.php#2026-10-23-1"],
  ["PHP2Shell advisory", "Cyber Security Agency of Singapore, updated 23 October 2026", "https://www.csa.gov.sg/alerts-and-advisories/alerts/al-2026-151/"],
];

const DESCRIPTION = "A plain-English guide to PHP2Shell for businesses, with authoritative sources and an enquiry-only route to ask about a scoped assessment.";

export default function Php2Shell({ onNavigate }) {
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute("content");
    document.title = "PHP2Shell Exposure Assessment | AskMax Security";
    descriptionMeta?.setAttribute("content", DESCRIPTION);

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null && descriptionMeta) {
        descriptionMeta.setAttribute("content", previousDescription);
      }
    };
  }, []);

  const requestEnquiry = (message = "PHP2Shell Exposure Assessment enquiry") => {
    const enquiryMessage = message && message !== "php2shell"
      ? message
      : "PHP2Shell Exposure Assessment enquiry";
    onNavigate("/bespoke", {
      enquiry: {
        service: "php2shell",
        message: enquiryMessage,
      },
    });
  };

  return (
    <>
      <NavBar mode="php2shell" onNavigate={onNavigate} onSelectService={requestEnquiry} />
      <main className="bespoke-page php2shell-page">
        <section className="bespoke-page__hero container php2shell-page__hero" id="overview">
          <div className="bespoke-page__hero-copy">
            <p className="bespoke-page__eyebrow">Urgent security advisory</p>
            <h1>Your business website could be at risk, even if you have never heard of PHP.</h1>
            <p className="bespoke-page__hero-lede">
              A serious security weakness has been found in software used by many websites. It is known as PHP2Shell.
            </p>
            <p className="php2shell-page__hero-copy">
              If your business website uses an affected version of PHP that has not been properly patched, an attacker may be able to access the systems behind it without a password.
            </p>
            <p className="php2shell-page__hero-copy">
              You do not need to understand the technology to find out whether you should take action. This guide explains what may need checking and what to do next.
            </p>
            <div className="php2shell-page__hero-actions">
              <button className="btn btn-primary" type="button" onClick={() => requestEnquiry()}>
                Enquire about an assessment
              </button>
              <a className="btn btn-secondary" href="#advisory">Understand the Problem</a>
            </div>
            <p className="php2shell-page__hero-note">PHP2Shell / Security advisory / October 2026. This page does not scan your website.</p>
          </div>
          <aside className="php2shell-page__summary" aria-label="What to know about PHP2Shell">
            <p className="bespoke-page__eyebrow">In plain English</p>
            <h2>Your website may use PHP without you knowing.</h2>
            <p>Your website developer or hosting company can help confirm what it runs and whether the relevant updates are installed.</p>
            <a href="#check">Not sure? See what to do next</a>
          </aside>
        </section>

        <section className="bespoke-page__section container" id="advisory" aria-labelledby="php2shell-advisory-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-advisory-title">What&apos;s happened, and why does it matter?</h2>
            <p>
              PHP is software that helps many websites work behind the scenes. You might never have heard of it, even if your business website uses it every day.
            </p>
          </div>
          <p className="php2shell-page__plain-explanation">
            Security researchers found serious weaknesses in certain PHP installations. Under vulnerable conditions, someone on the internet could potentially run unauthorised instructions on a server supporting a website. This is called PHP2Shell.
          </p>
          <p className="php2shell-page__qualification">This does not mean every website using PHP is vulnerable or has been attacked. The software version, installed updates and setup all matter.</p>
        </section>

        <section className="bespoke-page__section container" aria-labelledby="php2shell-impact-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-impact-title">What could this mean for your business?</h2>
            <p>These are possible consequences if a vulnerable system is successfully exploited. They are not evidence that your website has been attacked.</p>
          </div>
          <div className="php2shell-page__impact-grid">
            <article><span>01</span><h3>Customer information</h3><p>Information held by an affected website could potentially be exposed.</p></article>
            <article><span>02</span><h3>Website disruption</h3><p>An attacker could interfere with your website or online services.</p></article>
            <article><span>03</span><h3>Unauthorised changes</h3><p>Website files or connected systems could potentially be changed or misused.</p></article>
            <article><span>04</span><h3>Business interruption</h3><p>A security incident can mean lost time, unexpected costs and disruption for customers.</p></article>
          </div>
        </section>

        <section className="bespoke-page__section container" id="businesses" aria-labelledby="php2shell-businesses-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-businesses-title">This isn&apos;t just a problem for big technology companies.</h2>
            <p>
              You do not need to run a technology company to be affected by a website security issue. Your site may have been built by an agency or maintained by your hosting provider. You might not know what software it uses.
            </p>
          </div>
          <p className="php2shell-page__plain-explanation"><strong>You could be using PHP without knowing it.</strong> These examples describe organisations that might use PHP. They do not establish that a website is vulnerable.</p>
          <div className="php2shell-page__business-grid">
            <article><h3>Restaurants and hotels</h3><p>Your website might take bookings, answer customer questions or accept online orders.</p></article>
            <article><h3>Online shops</h3><p>Some shops use PHP-based software, including WordPress and WooCommerce setups. That alone does not mean the shop is vulnerable.</p></article>
            <article><h3>Solicitors and accountants</h3><p>Your public website or a client portal might rely on PHP software.</p></article>
            <article><h3>Small local businesses</h3><p>A simple website built several years ago may still use software that needs checking.</p></article>
            <article><h3>Growing businesses</h3><p>You may have several websites, customer portals or internal applications.</p></article>
            <article><h3>Larger organisations</h3><p>Different departments may use separate websites, suppliers and hosting companies.</p></article>
          </div>
        </section>

        <section className="bespoke-page__section container" id="check" aria-labelledby="php2shell-check-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-check-title">Do you know whether your website uses PHP?</h2>
            <p>
              The important question isn&apos;t whether your business is definitely at risk. It&apos;s whether anyone has checked.
            </p>
          </div>
          <div className="php2shell-page__decision-grid">
            <article>
              <p className="php2shell-page__decision-label">I know my site uses PHP</p>
              <p>Ask your developer or hosting company whether the relevant security updates are installed. Check the package vendor&apos;s advice too.</p>
            </article>
            <article>
              <p className="php2shell-page__decision-label">I don&apos;t think it uses PHP</p>
              <p>If that is confirmed, PHP2Shell may not apply to that website. Ask whether any other services or bundled components need checking.</p>
            </article>
            <article>
              <p className="php2shell-page__decision-label">I&apos;m not sure</p>
              <p>That&apos;s common. Your website developer or hosting provider can help establish what software is running. This guide explains what evidence to request.</p>
              <button className="btn btn-secondary" type="button" onClick={() => requestEnquiry("Question about whether my website uses PHP / PHP2Shell")}>Ask AskMax a question</button>
            </article>
          </div>
        </section>

        <section className="bespoke-page__section container" aria-labelledby="php2shell-urgency-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-urgency-title">Why check now?</h2>
            <p>PHP published fixes on 21 October 2026 and urged users to update. On 23 October, Singapore&apos;s Cyber Security Agency reported widespread exploitation attempts related to PHP2Shell. That is a report about activity targeting the vulnerabilities, not evidence that your website was reached.</p>
          </div>
          <div className="php2shell-page__advice">
            <strong>Do not wait for an assessment to apply an available security update.</strong>
            <p>If your hosting provider confirms your site is already appropriately patched, you may not need a paid assessment for these vulnerabilities. Ask your provider now if you have not checked.</p>
          </div>
        </section>

        <section className="bespoke-page__section container" id="research" aria-labelledby="php2shell-research-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-research-title">What the security authorities are saying</h2>
            <p>These original advisories recommend identifying PHP systems and applying updates where needed. They do not say every PHP website can be exploited.</p>
          </div>
          <div className="php2shell-page__research-grid">
            {RESEARCH_CARDS.map((source) => (
              <article key={source.href}>
                <p className="php2shell-page__source-date">{source.date}</p>
                <h3>{source.name}</h3>
                <p>{source.summary}</p>
                <a href={source.href} target="_blank" rel="noreferrer">Read the original advisory</a>
              </article>
            ))}
          </div>
          <details className="php2shell-page__technical">
            <summary>Technical details and original research</summary>
            <div className="php2shell-page__technical-body">
              <p>PHP describes two critical vulnerabilities, both rated CVSS v4.0 9.2. CVE scores describe technical severity, not the risk to an individual website.</p>
              <ul>
                <li><strong>CVE-2026-109618:</strong> memory corruption when out-of-memory handling interrupts PHP request processing.</li>
                <li><strong>CVE-2026-109619:</strong> stale request state can be accessed by a later request handled by the same worker.</li>
              </ul>
              <p>PHP lists 8.5.12, 8.4.27, 8.3.36 and 8.2.35 as fixed source releases. Distribution and long-term-support vendors may provide backported fixes. PHP 8.1 and older are end-of-life upstream; confirm the status of vendor packages with that vendor.</p>
              <p>PHP separately published an informational notice on 23 October about CVE-2026-115560, which it described as an unlikely risk involving an unusual configuration and application combination. It is distinct from the two PHP2Shell vulnerabilities described here.</p>
              <ul className="php2shell-page__technical-sources">
                {TECHNICAL_SOURCES.map(([name, publisher, href]) => (
                  <li key={href}><a href={href} target="_blank" rel="noreferrer">{name}</a><span>{publisher}</span></li>
                ))}
              </ul>
            </div>
          </details>
          <aside className="php2shell-page__advice" id="october-23-update" aria-labelledby="october-23-update-title">
            <strong id="october-23-update-title">23 October 2026 update: PHP session upload progress</strong>
            <p>
              PHP described an additional, unlikely RCE vector (CVE-2026-115560). It requires a specific combination: the configured <code>session.serialize_handler</code> is <code>php_binary</code> or <code>php_serialize</code>, application code switches it to <code>php</code> with <code>ini_set()</code> before <code>session_start()</code>, and session upload progress remains enabled. PHP says applications that never change the handler at runtime can keep this feature enabled; this does not mean every PHP installation is vulnerable.
            </p>
            <p>
              Administrators should check the session configuration and application code for that runtime switch. PHP recommends setting <code>session.upload_progress.enabled=0</code> until the next regular bugfix release. As of 29 October, PHP had not published a later regular bugfix release changing this advice; its 26 October Windows package rebuild kept the same PHP runtime versions.
            </p>
            <p><a href="https://www.php.net/archive/2026.php#2026-10-23-1" target="_blank" rel="noreferrer">Read PHP&apos;s 23 October advisory</a></p>
          </aside>
        </section>

        <section className="bespoke-page__section container" id="assessment" aria-labelledby="php2shell-assessment-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-assessment-title">You run your business. Get clarity about the security risk.</h2>
            <p>You should not need to become a cybersecurity expert to understand whether your website needs attention. Our team will review your enquiry, agree what can be assessed and confirm the required authorisation before technical work begins.</p>
          </div>
          <div className="bespoke-page__steps" aria-label="Assessment enquiry steps">
            {ASSESSMENT_STEPS.map((step) => (
              <article className="bespoke-page__step" key={step.number}>
                <p className="bespoke-page__step-number">{step.number}</p>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
          <div className="bespoke-page__blueprint php2shell-page__deliverable">
            <div>
              <p className="bespoke-page__eyebrow">Agreed scope before assessment</p>
              <h2>A clear report, not a wall of technical language.</h2>
              <p>An assessment covers one agreed website or hosting environment. The relevant evidence and boundaries are confirmed with you before work begins.</p>
            </div>
            <ul>
              <li>Review of relevant PHP software and update evidence</li>
              <li>Assessment of the available evidence for the agreed scope</li>
              <li>A plain-English report of what was checked and found</li>
              <li>Practical recommendations and what remains uncertain</li>
            </ul>
          </div>
          <p className="bespoke-page__limitation">No scan or access happens from this page. Work starts only after scope and written authorisation are confirmed. Remediation is quoted separately.</p>
        </section>

        <section className="bespoke-page__section container" id="offer" aria-labelledby="php2shell-offer-title">
          <div className="php2shell-page__offer">
            <div>
              <p className="bespoke-page__eyebrow">PHP2Shell Exposure Assessment</p>
              <h2 id="php2shell-offer-title">Get clarity about your website&apos;s security.</h2>
              <p className="php2shell-page__price">&euro;299 <span>excluding applicable VAT</span></p>
              <ul className="php2shell-page__included">
                <li>One agreed website or hosting environment</li>
                <li>Review of relevant PHP software and update information</li>
                <li>A plain-English findings report with practical next steps</li>
              </ul>
              <p>Remediation, if needed, is quoted separately.</p>
            </div>
            <div className="php2shell-page__offer-action">
              <button className="btn btn-primary" type="button" onClick={() => requestEnquiry("PHP2Shell Exposure Assessment enquiry - displayed price EUR 299 excluding applicable VAT") }>
                Enquire about assessment - &euro;299
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => requestEnquiry("Question about PHP2Shell before requesting an assessment") }>
                Ask a Question First
              </button>
              <p>This opens the contact form with PHP2Shell Exposure Assessment selected. It is an enquiry, not a booking or payment. Scope and timing are agreed before any work begins.</p>
            </div>
          </div>
        </section>

        <section className="bespoke-page__section container" aria-labelledby="php2shell-reassurance-title">
          <div className="bespoke-page__section-head">
            <h2 id="php2shell-reassurance-title">What if my website is already protected?</h2>
            <p>That&apos;s good news. Your hosting provider may already have applied the necessary updates. If they confirm your website is appropriately patched, you may not need further work for this particular vulnerability.</p>
          </div>
          <p className="php2shell-page__plain-explanation">Any agreed assessment would explain what was checked, what was not and whether further action may be necessary. You may not need paid assessment work if your provider confirms the relevant updates are installed.</p>
        </section>

        <section className="bespoke-page__close container">
          <p>Do not delay an available security update while waiting for an assessment. This page does not establish whether your website is vulnerable or has been attacked.</p>
        </section>
      </main>

      <footer className="bespoke-page__footer">
        <div className="container bespoke-page__footer-row">
          <BrandMark onClick={() => onNavigate("/")} />
          <span>AskMax Security / PHP2Shell Exposure Assessment</span>
          <a href="/legal-notice" onClick={(event) => { event.preventDefault(); onNavigate("/legal-notice"); }}>Legal Notice</a>
          <a href="/privacy/" onClick={(event) => { event.preventDefault(); onNavigate("/privacy"); }}>Privacy Notice</a>
          <span>Enquiry only / Scope and timing agreed before work</span>
        </div>
      </footer>
    </>
  );
}